import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { api } from '../services/api';
import AppFooter from '../components/AppFooter';
import {
  LogOut, BookOpen, Settings, AlertCircle, KeyRound, Link2, RefreshCw,
  Mail, Megaphone, Calendar, CalendarDays, AlertTriangle, CreditCard, MessageCircle, Users2,
} from 'lucide-react';

interface PairedChild {
  relation_id: string;
  id: string;
  name: string;
  email: string;
  class: string | null;
  created_at: string;
}

interface EpCreds { linked: boolean; last_sync_at?: string | null; }

interface TileStats { total: number; unread?: number; new: number; }
interface ChildSummary {
  external_id: string;
  name: string;
  class_display: string | null;
  class_normalized: string | null;
  tiles: {
    elternbriefe: TileStats;
    schwarzes_brett: TileStats;
    termine: TileStats;
    stundenplan: TileStats;
    vertretungsplan: TileStats;
    zahlungen: TileStats;
    kommunikation: TileStats;
    kollegium: TileStats;
  };
}

type TileKey = keyof ChildSummary['tiles'];

const TILES: Array<{ key: TileKey; to: string; icon: React.ReactNode; label: string; color: string }> = [
  { key: 'elternbriefe', to: '/eltern-portal/briefe', icon: <Mail className="w-6 h-6" />, label: 'Elternbriefe', color: 'text-blue-400' },
  { key: 'schwarzes_brett', to: '/eltern-portal/brett', icon: <Megaphone className="w-6 h-6" />, label: 'Schwarzes Brett', color: 'text-orange-400' },
  { key: 'termine', to: '/eltern-portal/termine', icon: <Calendar className="w-6 h-6" />, label: 'Termine', color: 'text-purple-400' },
  { key: 'stundenplan', to: '/eltern-portal/stundenplan', icon: <CalendarDays className="w-6 h-6" />, label: 'Stundenplan', color: 'text-cyan-400' },
  { key: 'vertretungsplan', to: '/eltern-portal/vertretungsplan', icon: <AlertTriangle className="w-6 h-6" />, label: 'Vertretungsplan', color: 'text-yellow-400' },
  { key: 'zahlungen', to: '/eltern-portal/zahlungen', icon: <CreditCard className="w-6 h-6" />, label: 'Zahlungen', color: 'text-green-400' },
  { key: 'kommunikation', to: '/eltern-portal/kommunikation', icon: <MessageCircle className="w-6 h-6" />, label: 'Kommunikation', color: 'text-pink-400' },
  { key: 'kollegium', to: '/eltern-portal/kollegium', icon: <Users2 className="w-6 h-6" />, label: 'Lehrkräfte', color: 'text-gray-300' },
];

function Tile({ to, icon, label, color, stats }: { to: string; icon: React.ReactNode; label: string; color: string; stats: TileStats }) {
  const hasNew = (stats.new ?? 0) > 0;
  const hasUnread = (stats.unread ?? 0) > 0;
  const highlight = hasNew || hasUnread;
  return (
    <Link to={to}
      className={`relative rounded-lg p-4 text-center transition border ${
        highlight
          ? 'bg-red-500/10 border-red-500/50 hover:bg-red-500/20 ring-1 ring-red-500/40'
          : 'bg-gray-800 hover:bg-gray-700 border-gray-700'
      }`}>
      <div className={`mx-auto mb-2 ${color}`}>{icon}</div>
      <div className="text-sm font-medium text-white">{label}</div>
      <div className="mt-1 text-xs text-gray-400">{stats.total > 0 ? `${stats.total} insgesamt` : '—'}</div>
      {hasNew && (
        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[22px] px-1.5 py-0.5 shadow-lg animate-pulse">
          {stats.new} NEU
        </span>
      )}
      {!hasNew && hasUnread && (
        <span className="absolute -top-2 -right-2 bg-yellow-500 text-gray-900 text-[10px] font-bold rounded-full min-w-[22px] px-1.5 py-0.5 shadow">
          {stats.unread}
        </span>
      )}
    </Link>
  );
}

function ChildSection({ child, pairedName, pairedSince, lastSync, onSync, busy }: {
  child: ChildSummary;
  pairedName?: string | null;
  pairedSince?: string | null;
  lastSync?: string | null;
  onSync?: () => void;
  busy?: boolean;
}) {
  const anyNew = Object.values(child.tiles).some(t => (t.new ?? 0) > 0);
  const initials = child.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <section className={`rounded-lg border ${anyNew ? 'border-red-500/50 bg-red-500/5' : 'border-gray-700 bg-gray-800/50'} p-5 space-y-4`}>
      <header className="flex items-center gap-3 flex-wrap">
        <div className="w-12 h-12 rounded-full bg-blue-500/20 text-blue-200 flex items-center justify-center font-bold text-xl">
          {initials}
        </div>
        <div className="flex-1 min-w-[180px]">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2 flex-wrap">
            {child.name}
            {anyNew && <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded animate-pulse font-semibold">Neuigkeiten</span>}
          </h2>
          <div className="flex flex-wrap gap-2 mt-1 text-xs">
            {child.class_display && <span className="bg-gray-700 text-gray-200 px-1.5 py-0.5 rounded">Klasse {child.class_display}</span>}
            {lastSync && (
              <span className="bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded">
                zuletzt synchronisiert: {new Date(lastSync).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
              </span>
            )}
            {pairedSince && <span className="bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded">Konto verknüpft seit {new Date(pairedSince).toLocaleDateString('de-DE')}</span>}
            {!pairedName && <span className="bg-yellow-500/20 text-yellow-200 px-1.5 py-0.5 rounded">Nicht mit HSG-Account verknüpft</span>}
          </div>
        </div>
        {onSync && (
          <button onClick={onSync} disabled={busy}
            className="ml-auto bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-2 shrink-0">
            <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} /> Jetzt synchronisieren
          </button>
        )}
      </header>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {TILES.map(t => (
          <Tile key={t.key} to={t.to} icon={t.icon} label={t.label} color={t.color} stats={child.tiles[t.key]} />
        ))}
      </div>
    </section>
  );
}

const ParentDashboardPage: React.FC = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [paired, setPaired] = useState<PairedChild[]>([]);
  const [summary, setSummary] = useState<ChildSummary[]>([]);
  const [ep, setEp] = useState<EpCreds>({ linked: false });
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogout = () => { logout(); navigate('/login'); };

  const load = async () => {
    try {
      const [c, creds, sum] = await Promise.all([
        api<{ children: PairedChild[] }>('/api/me/children').catch(() => ({ children: [] })),
        api<EpCreds>('/api/eltern-portal/credentials').catch(() => ({ linked: false })),
        api<{ children: ChildSummary[] }>('/api/eltern-portal/summary').catch(() => ({ children: [] })),
      ]);
      setPaired(c.children);
      setEp(creds);
      setSummary(sum.children);
    } catch (err: any) {
      setError(err?.message || 'Fehler beim Laden');
    }
  };

  useEffect(() => { load(); }, []);

  const triggerSync = async () => {
    setBusy(true); setError(null);
    try {
      await api('/api/eltern-portal/sync', { method: 'POST' });
      await load();
    } catch (err: any) {
      setError(err?.message || 'Sync fehlgeschlagen');
    } finally { setBusy(false); }
  };

  // Build unified child list: prefer eltern-portal children (with badges), fall back to paired
  // (account-paired) children that have no portal match.
  const childList = useMemo(() => {
    const list: Array<{ summary: ChildSummary | null; paired: PairedChild | null; name: string; klass: string | null }> = [];
    for (const s of summary) {
      // try to match by normalized name OR class
      const match = paired.find(p =>
        p.name.toLowerCase() === s.name.toLowerCase()
        || (s.class_normalized && p.class && p.class.toLowerCase().replace(/\s+/g, '') === s.class_normalized)
      ) || null;
      list.push({ summary: s, paired: match, name: s.name, klass: s.class_display });
    }
    // Add any paired-only children that weren't matched above
    const matchedIds = new Set(list.map(l => l.paired?.id).filter(Boolean));
    for (const p of paired) {
      if (!matchedIds.has(p.id)) {
        list.push({ summary: null, paired: p, name: p.name, klass: p.class });
      }
    }
    return list;
  }, [summary, paired]);

  const hasAnyChild = childList.length > 0;
  const selected = childList[selectedIdx] ?? childList[0];

  return (
    <div className="min-h-screen bg-gray-900">
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-blue-500" />
            <h1 className="text-2xl font-bold text-white">Hausaufgabenheft · Eltern</h1>
          </div>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/einstellungen" className="text-gray-300 hover:text-white flex items-center gap-1">
              <Settings className="w-4 h-4" /> Einstellungen
            </Link>
            <div className="text-right hidden sm:block">
              <p className="text-xs text-gray-400">Eltern-Account</p>
              <p className="text-white font-semibold">{user?.name}</p>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-2 bg-gray-700 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm">
              <LogOut className="w-4 h-4" />
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <section>
          <h2 className="text-2xl font-bold text-white mb-1">Hallo {user?.name}! 👋</h2>
          <p className="text-gray-400">Übersicht deiner verknüpften Kinder.</p>
        </section>

        {error && <p className="text-red-400 text-sm">{error}</p>}

        {/* Kein Kind verknüpft → Pairing-CTA */}
        {!hasAnyChild && (
          <section className="bg-yellow-500/10 border border-yellow-500/40 rounded-lg p-6">
            <h3 className="font-semibold text-yellow-200 flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5" /> Noch kein Kind verknüpft
            </h3>
            <p className="text-yellow-100/90 text-sm mb-4">
              Lass dir von deinem Kind einen 8-stelligen Pairing-Code aus dessen Einstellungen
              (<em>Eltern / Kind</em>-Tab) geben und trage ihn bei dir ein. Oder verbinde zuerst dein
              Eltern-Portal — dann holen wir die Kinder direkt dort.
            </p>
            <div className="flex flex-wrap gap-2">
              <Link to="/einstellungen" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg">
                <KeyRound className="w-4 h-4" /> Pairing-Code einlösen
              </Link>
              {!ep.linked && (
                <Link to="/einstellungen" className="inline-flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg">
                  <Link2 className="w-4 h-4" /> Eltern-Portal verknüpfen
                </Link>
              )}
            </div>
          </section>
        )}

        {/* Kind-Tabs wenn mehrere Kinder */}
        {childList.length > 1 && (
          <div className="flex flex-wrap gap-2">
            {childList.map((c, i) => {
              const anyNew = c.summary && Object.values(c.summary.tiles).some(t => (t.new ?? 0) > 0);
              return (
                <button key={c.name + i} onClick={() => setSelectedIdx(i)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
                    selectedIdx === i ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}>
                  {c.name}
                  {c.klass && <span className="text-xs opacity-80">({c.klass})</span>}
                  {anyNew && <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
                </button>
              );
            })}
          </div>
        )}

        {/* Kind-Sektion(en) */}
        {selected && selected.summary && (
          <ChildSection
            child={selected.summary}
            pairedName={selected.paired?.name ?? null}
            pairedSince={selected.paired?.created_at ?? null}
            lastSync={ep.last_sync_at ?? null}
            onSync={ep.linked ? triggerSync : undefined}
            busy={busy}
          />
        )}

        {/* Paired-only Fallback: Kind verknüpft, aber kein Eltern-Portal */}
        {selected && !selected.summary && selected.paired && (
          <section className="bg-gray-800 border border-gray-700 rounded-lg p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 text-blue-200 flex items-center justify-center font-bold text-xl">
                {selected.paired.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{selected.paired.name}</h3>
                <p className="text-xs text-gray-400">{selected.paired.email}</p>
                <div className="flex flex-wrap gap-2 mt-1 text-xs">
                  {selected.paired.class && <span className="bg-gray-700 text-gray-200 px-1.5 py-0.5 rounded">Klasse {selected.paired.class}</span>}
                  <span className="bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded">verknüpft seit {new Date(selected.paired.created_at).toLocaleDateString('de-DE')}</span>
                </div>
              </div>
            </div>
            <p className="mt-4 text-sm text-gray-400">
              Für Eltern-Portal-Badges (Elternbriefe, Zahlungen, Termine etc.) verknüpfe dein offizielles
              Eltern-Portal. Die Daten tauchen dann hier pro Kind auf.
            </p>
            {!ep.linked && (
              <Link to="/einstellungen" className="mt-3 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-3 py-2 rounded-lg">
                <Link2 className="w-4 h-4" /> Eltern-Portal verknüpfen
              </Link>
            )}
          </section>
        )}

        {/* Eltern-Portal-CTA — nur wenn keins verknüpft (sonst ist alles in den Tiles) */}
        {!ep.linked && hasAnyChild && (
          <section className="bg-gray-800 border border-gray-700 rounded-lg p-6">
            <h3 className="font-semibold text-white mb-2 flex items-center gap-2"><Link2 className="w-4 h-4 text-blue-400" /> Eltern-Portal synchronisieren</h3>
            <p className="text-sm text-gray-300 mb-3">
              Verknüpfe dein offizielles Eltern-Portal (eltern-portal.org) und hole Elternbriefe, Schwarzes Brett,
              Zahlungen, Termine, Kommunikation und Stundenplan direkt pro Kind in dein Dashboard.
            </p>
            <Link to="/einstellungen" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold">
              <Link2 className="w-4 h-4" /> Eltern-Portal einrichten
            </Link>
          </section>
        )}

        <AppFooter />
      </main>
    </div>
  );
};

export default ParentDashboardPage;
